import { useEffect, useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import type { LocationSearchPractice } from '@/react-query/useReferralInfo';

import { useLocationSpecialistSearch } from '../../hooks/useLocationSpecialistSearch';
import type { ReferralFormValues } from '../../types';
import { LocationSearchFilters } from './LocationSearchFilters';
import { formatPracticeAddress } from './locationSearchUtils';
import { SpecialistSelectionForm } from './SpecialistSelectionForm';

interface TreatingSpecialistTabV2Props {
  onPracticeSelect?: (
    practice:
      | {
          practice_id: number;
          practice_name: string;
          referral_practice_address?: string;
          referral_practice_phone?: string;
        }
      | undefined,
  ) => void;
  videaPracticeId: string;
}

export const TreatingSpecialistTabV2 = ({
  onPracticeSelect,
  videaPracticeId,
}: TreatingSpecialistTabV2Props) => {
  const {
    setValue,
    formState: { errors },
  } = useFormContext<ReferralFormValues>();

  const [selectedPractice, setSelectedPractice] =
    useState<LocationSearchPractice | null>(null);

  // Watch the selected values from the form
  const selectedSpecialtyId = useWatch({ name: 'specialtyId' });
  const selectedPracticeId = useWatch({ name: 'referralPracticeId' });
  const useNextAvailableSpecialist = useWatch({
    name: 'useNextAvailableSpecialist',
  });
  const selectedSpecialistId = useWatch({ name: 'referralSpecialistId' });

  // Use custom hook for location-based search logic
  const {
    postalCode,
    setPostalCode,
    debouncedPostalCode,
    radius,
    setRadius,
    locationSearchResults,
    specialties,
    practices,
    specialists,
    isSearchLoading,
    isSearchFetching,
    isSpecialtiesLoading,
    isPracticesLoading,
  } = useLocationSpecialistSearch({
    videaPracticeId,
    selectedSpecialtyId,
    selectedPractice,
  });

  // Reset selected practice when postal code or radius changes
  useEffect(() => {
    if (selectedPractice) {
      setSelectedPractice(null);
      setValue('referralPracticeId', undefined);
      setValue('referralSpecialistId', undefined);
      setValue('useNextAvailableSpecialist', false);
      setValue('referralPracticeAddress', '');
      setValue('referralPracticePhone', '');
      onPracticeSelect?.(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPostalCode, radius]);

  // Handler functions
  const handleSpecialtyChange = (specialtyId: string) => {
    setValue('specialtyId', parseInt(specialtyId, 10));
    setValue('proceduresIds', []);
    setValue('referralPracticeId', undefined);
    setValue('referralSpecialistId', undefined);
    setValue('useNextAvailableSpecialist', false);
    setValue('referralPracticeAddress', '');
    setValue('referralPracticePhone', '');
    setSelectedPractice(null);
    onPracticeSelect?.(undefined);
  };

  const handlePracticeChange = (practiceId: string) => {
    const practice = practices.find((p) => p.id.toString() === practiceId);

    setValue('referralPracticeId', parseInt(practiceId, 10));
    setValue('referralSpecialistId', undefined);
    setValue('useNextAvailableSpecialist', false);

    if (practice) {
      const fullAddress = formatPracticeAddress(practice);

      setValue('referralPracticeAddress', fullAddress || '');
      setValue('referralPracticePhone', practice.phone || '');
      setSelectedPractice(practice);
      onPracticeSelect?.({
        practice_id: practice.id,
        practice_name: practice.practiceName,
        referral_practice_address: fullAddress,
        referral_practice_phone: practice.phone || undefined,
      });
    } else {
      setValue('referralPracticeAddress', '');
      setValue('referralPracticePhone', '');
      setSelectedPractice(null);
      onPracticeSelect?.(undefined);
    }
  };

  const handleSpecialistChange = (specialistId: string) => {
    setValue('referralSpecialistId', parseInt(specialistId, 10));
  };

  const handleNextAvailableToggle = (checked: boolean) => {
    setValue('useNextAvailableSpecialist', checked);
    if (checked) {
      setValue('referralSpecialistId', undefined);
    }
  };

  return (
    <div className="vai-flex vai-gap-6">
      <LocationSearchFilters
        postalCode={postalCode}
        onPostalCodeChange={setPostalCode}
        radius={radius}
        onRadiusChange={setRadius}
        isSearching={isSearchFetching}
      />

      <SpecialistSelectionForm
        specialties={specialties}
        practices={practices}
        specialists={specialists}
        selectedSpecialtyId={selectedSpecialtyId}
        selectedPracticeId={selectedPracticeId}
        selectedSpecialistId={selectedSpecialistId}
        useNextAvailableSpecialist={useNextAvailableSpecialist}
        onSpecialtyChange={handleSpecialtyChange}
        onPracticeChange={handlePracticeChange}
        onSpecialistChange={handleSpecialistChange}
        onNextAvailableToggle={handleNextAvailableToggle}
        postalCode={postalCode}
        debouncedPostalCode={debouncedPostalCode}
        isSpecialtiesLoading={isSpecialtiesLoading}
        isPracticesLoading={isPracticesLoading}
        isSearchLoading={isSearchLoading}
        locationSearchResults={locationSearchResults}
        errors={errors}
      />
    </div>
  );
};
